/* elemence-join-waitlist.js — wire the /join waitlist form to Klaviyo
   (client-side subscribe) and capture a ?ref= referral code for attribution.
   No backend: calls Klaviyo's public /client/subscriptions + /client/profiles
   endpoints directly from the browser. company_id (Public Site ID) and the
   list id are read from data-klaviyo-company / data-klaviyo-list on the
   <form> (added by the Liquid section), never hardcoded here.
   Assumes referral links point directly at /join?ref=CODE — this script
   only runs on the join page. See Docs/Research/26 for the full API spec. */
(function () {
  const KLAVIYO_REVISION = '2026-04-15';
  const REF_STORAGE_KEY = 'elemence_ref';
  const JOINED_STORAGE_KEY = 'elemence_alpha_joined';
  const JOINED_EMAILS_KEY = 'elemence_joined_emails';

  // localStorage can throw (private browsing, storage disabled by policy) —
  // every access is wrapped so the form still fully works without it.
  function safeGetItem(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function safeSetItem(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      /* ignore — private mode or storage disabled */
    }
  }

  function safeRemoveItem(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (e) {
      /* ignore */
    }
  }

  // Let the header CTA (or any other listener) update itself the moment a
  // signup succeeds, without waiting for a full page reload.
  function notifyJoined() {
    try {
      document.dispatchEvent(new CustomEvent('elemence:alpha-joined'));
    } catch (e) {
      /* very old browsers without the CustomEvent ctor — non-fatal, header just won't live-update */
    }
  }

  function getJoinedEmails() {
    try {
      const raw = safeGetItem(JOINED_EMAILS_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function isEmailAlreadyJoined(normalizedEmail) {
    return getJoinedEmails().indexOf(normalizedEmail) !== -1;
  }

  function rememberJoinedEmail(normalizedEmail) {
    const emails = getJoinedEmails();
    if (emails.indexOf(normalizedEmail) === -1) {
      emails.push(normalizedEmail);
      safeSetItem(JOINED_EMAILS_KEY, JSON.stringify(emails));
    }
  }

  // Security: cap length + strip control chars so a crafted ?ref= can't
  // smuggle an oversized value or control sequences into a Klaviyo property.
  function sanitizeRef(raw) {
    if (!raw) return '';
    let value = String(raw).trim();
    value = value.replace(/[\x00-\x1F\x7F]/g, '');
    if (value.length > 200) value = value.slice(0, 200);
    return value;
  }

  function readRefFromUrl() {
    try {
      const params = new URLSearchParams(window.location.search);
      return sanitizeRef(params.get('ref'));
    } catch (e) {
      return '';
    }
  }

  // Called once on load: persist a fresh ?ref= so it survives a later
  // ref-less visit to /join (e.g. user bookmarks the page and returns).
  function captureRefOnLoad() {
    const ref = readRefFromUrl();
    if (ref) safeSetItem(REF_STORAGE_KEY, ref);
  }

  // Current URL ref wins over a stored one; falls back to whatever was
  // captured on an earlier visit.
  function getActiveRef() {
    const fromUrl = readRefFromUrl();
    if (fromUrl) return fromUrl;
    return sanitizeRef(safeGetItem(REF_STORAGE_KEY) || '');
  }

  function setLoading(form, button, isLoading) {
    form.dataset.joinSubmitting = isLoading ? '1' : '0';
    if (!button) return;
    button.disabled = isLoading;
    if (isLoading) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  }

  // The success card lives INSIDE the <form>, so hiding the whole form
  // (form.hidden = true) would cascade [hidden]{display:none} onto it and the
  // user would never see the thank-you. Instead keep the form visible and hide
  // just the input row — inline display:none beats the CSS rule
  // `.elemence-join-waitlist__field { display: flex }` (no !important there).
  // The success card is STATIC markup in the section (check + title + sub), so
  // nothing is injected here — we only toggle visibility.
  function revealSuccessState(form) {
    const success = form.querySelector('[data-join-waitlist-success]');
    const errorEl = form.querySelector('[data-join-waitlist-error]');
    const warningEl = form.querySelector('[data-join-waitlist-warning]');
    const field = form.querySelector('.elemence-join-waitlist__field');
    if (field) field.style.display = 'none';
    if (errorEl) errorEl.hidden = true;
    if (warningEl) warningEl.hidden = true;
    if (success) success.hidden = false;
  }

  function showSuccess(form) {
    revealSuccessState(form);
  }

  // Duplicate email (already joined in THIS browser) — unlike success, the field
  // STAYS visible and the form stays retryable: flag the input with a --dup class
  // and reveal an inline warning, send no request. Cleared by clearDupState() the
  // moment the user edits the email or submits again.
  function showAlreadyJoined(form) {
    const input = form.querySelector('input[name="email"]');
    const warningEl = form.querySelector('[data-join-waitlist-warning]');
    const errorEl = form.querySelector('[data-join-waitlist-error]');
    if (errorEl) errorEl.hidden = true;
    if (input) {
      input.classList.add('elemence-join-waitlist__input--dup');
      input.focus();
    }
    if (warningEl) warningEl.hidden = false;
  }

  // Reset the duplicate-email affordance so the user can immediately retry.
  function clearDupState(form) {
    const input = form.querySelector('input[name="email"]');
    const warningEl = form.querySelector('[data-join-waitlist-warning]');
    if (input) input.classList.remove('elemence-join-waitlist__input--dup');
    if (warningEl) warningEl.hidden = true;
  }

  // [data-join-waitlist-error] is added by a parallel task (T3) and may not
  // exist yet — query defensively, never throw, always still re-enable the
  // button (done by the caller via setLoading).
  function showError(form, message) {
    const errorEl = form.querySelector('[data-join-waitlist-error]');
    if (errorEl) {
      if (message) errorEl.textContent = message;
      errorEl.hidden = false;
    } else {
      console.warn('[join] no [data-join-waitlist-error] element found, error was:', message);
    }
  }

  async function subscribeToKlaviyo(companyId, listId, email) {
    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': KLAVIYO_REVISION },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            // `subscriptions` must live INSIDE profile.data.attributes, not beside
            // `profile` — Klaviyo (revision 2026-04-15) rejects it as a sibling with
            // 400 "'subscriptions' is not a valid field for the resource 'subscription'".
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                },
              },
            },
          },
          relationships: { list: { data: { type: 'list', id: listId } } },
        },
      }),
      signal: AbortSignal.timeout(7000),
    });
  }

  // Best-effort attribution only — custom properties (referrer) can't ride
  // the subscribe call, so this is a separate request. It must never gate
  // the success/error UI: any failure here is swallowed and just logged.
  async function sendReferrerProfile(companyId, email, ref) {
    try {
      await fetch('https://a.klaviyo.com/client/profiles/?company_id=' + encodeURIComponent(companyId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': KLAVIYO_REVISION },
        body: JSON.stringify({
          data: {
            type: 'profile',
            attributes: { email: email, properties: { referrer: ref } },
          },
        }),
        signal: AbortSignal.timeout(7000),
      });
    } catch (e) {
      console.warn('[join] referrer profile update failed (non-fatal):', e);
    }
  }

  // Dual-write (launch day 2026-09-06): every sign-up is ALSO posted to our own
  // intake backend (<data-api-base>/api/join) in parallel with the Klaviyo
  // subscribe. Klaviyo's Subscription Protection silently drops some sign-ups
  // (202, no profile) - the backend keeps every email regardless. Resolves to
  // true on 201, false on anything else; never throws.
  async function postJoinBackend(apiBase, email) {
    try {
      const res = await fetch(apiBase + '/api/join', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email,
          locale: (document.documentElement.getAttribute('lang') || '').slice(0, 20),
          page: (window.location.pathname || '').slice(0, 200),
        }),
        signal: AbortSignal.timeout(8000),
      });
      if (res.status === 201) return true;
      console.warn('[join] backend /api/join returned status', res.status);
      return false;
    } catch (e) {
      console.warn('[join] backend /api/join failed (non-fatal if Klaviyo accepted):', e);
      return false;
    }
  }

  function handleSubmit(event) {
    event.preventDefault();
    const form = event.currentTarget;
    if (form.dataset.joinSubmitting === '1') return;

    const emailInput = form.querySelector('input[name="email"]');
    const button = form.querySelector('button[type="submit"]');
    // Honeypot branch removed 2026-09-06: browser autofill filled the hidden
    // field and real sign-ups were silently faked as success (never sent).

    const companyId = form.dataset.klaviyoCompany;
    const listId = form.dataset.klaviyoList;
    if (!companyId || !listId) {
      console.error('[join] missing data-klaviyo-company / data-klaviyo-list on form — cannot subscribe');
      showError(form, form.dataset.msgUnavailable || 'Signup is temporarily unavailable. Please try again later.');
      return;
    }

    if (!emailInput || !emailInput.checkValidity()) {
      showError(form, form.dataset.msgInvalidEmail || 'Please enter a valid email address.');
      if (emailInput) emailInput.focus();
      return;
    }

    const email = emailInput.value.trim();

    const normalizedEmail = email.toLowerCase();
    if (isEmailAlreadyJoined(normalizedEmail)) {
      showAlreadyJoined(form);
      return;
    }

    const ref = getActiveRef();

    // Clear any stale error from a previous attempt before committing to send.
    const errorEl = form.querySelector('[data-join-waitlist-error]');
    if (errorEl) errorEl.hidden = true;

    setLoading(form, button, true);

    // Fire the best-effort referrer profile update WITHOUT awaiting it
    // before the subscribe call — attribution must never delay or block
    // signup. Only the subscribe call's outcome gates the UI.
    const tasks = [];
    if (ref) tasks.push(sendReferrerProfile(companyId, email, ref));

    // Klaviyo subscribe -> {ok, network}; never throws.
    const klaviyoTask = subscribeToKlaviyo(companyId, listId, email)
      .then((res) => {
        if (res.status !== 202) console.warn('[join] subscribe request returned status', res.status);
        return { ok: res.status === 202, network: false };
      })
      .catch((err) => {
        console.warn('[join] subscribe request failed:', err);
        return { ok: false, network: true };
      });

    // Our backend, in parallel (dual-write). Empty data-api-base = Klaviyo only.
    const apiBase = String(form.dataset.apiBase || '').replace(/\/+$/, '');
    const backendTask = apiBase ? postJoinBackend(apiBase, email) : Promise.resolve(false);

    // Success if EITHER side accepted the email (the backend keeps it even when
    // Klaviyo silently drops it); error only when both failed.
    Promise.all([klaviyoTask, backendTask]).then(([klaviyo, backendOk]) => {
      if (klaviyo.ok || backendOk) {
        if (!klaviyo.ok) console.warn('[join] Klaviyo did not accept, backend did - showing success');
        safeSetItem(JOINED_STORAGE_KEY, '1');
        notifyJoined();
        safeRemoveItem(REF_STORAGE_KEY);
        rememberJoinedEmail(normalizedEmail);
        showSuccess(form);
        return;
      }
      showError(form, klaviyo.network
        ? (form.dataset.msgNetwork || 'Something went wrong. Please check your connection and try again.')
        : (form.dataset.msgGeneric || 'Something went wrong. Please try again.'));
      setLoading(form, button, false);
    });

    Promise.allSettled(tasks);
  }

  function attach(form) {
    if (!form || form.dataset.joinInit === '1') return;
    form.dataset.joinInit = '1';
    form.addEventListener('submit', handleSubmit);
    // Editing the email clears the duplicate-email warning so a retry is instant.
    const emailInput = form.querySelector('input[name="email"]');
    if (emailInput) {
      emailInput.addEventListener('input', function () { clearDupState(form); });
    }
  }

  function init() {
    captureRefOnLoad();
    document.querySelectorAll('form[data-join-waitlist]').forEach(attach);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target && e.target.querySelectorAll) {
      e.target.querySelectorAll('form[data-join-waitlist]').forEach(attach);
    }
  });
})();
